/**
 * Everything a human or the model reads runs on Polish wall-clock time; the ISO instant stays
 * the machine key (umawiator input, DB, tool inputs). One place for the formatters so no
 * boundary silently falls back to the host timezone (Fly runs on UTC).
 */
export const TZ = 'Europe/Warsaw'

const dateKeyFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const timeFmt = new Intl.DateTimeFormat('pl-PL', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
})
const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', hour12: false })
const longDayFmt = new Intl.DateTimeFormat('pl-PL', {
  timeZone: TZ,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})
const weekdayLongFmt = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, weekday: 'long' })
const weekdayShortFmt = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, weekday: 'short' })
const dayNumFmt = new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, day: 'numeric' })

/** `YYYY-MM-DD` in Warsaw — day key for slot grouping, calendar grid and umawiator params. */
export const warsawDateKey = (iso: string | Date): string => dateKeyFmt.format(new Date(iso))

/** `HH:MM` in Warsaw — the only hour form we ever show or say. */
export const warsawTime = (iso: string | Date): string => timeFmt.format(new Date(iso))

/** Hour 0–23 in Warsaw (slot buckets, opening hours). */
export const warsawHour = (iso: string | Date): number =>
  Number.parseInt(hourFmt.format(new Date(iso)), 10) % 24

/** „poniedziałek, 26 sierpnia” */
export const warsawDay = (iso: string | Date): string => longDayFmt.format(new Date(iso))

/** „poniedziałek” */
export const warsawWeekday = (iso: string | Date): string => weekdayLongFmt.format(new Date(iso))

/** „pon” (no trailing dot) */
export const warsawWeekdayShort = (iso: string | Date): string =>
  weekdayShortFmt.format(new Date(iso)).replace('.', '')

/** Day of month as a number (calendar chips). */
export const warsawDayNumber = (iso: string | Date): number =>
  Number(dayNumFmt.format(new Date(iso)))

/**
 * „poniedziałek, 26 sierpnia, 16:00” — a whole term in prose. Used wherever an instant reaches
 * the model, which must never convert a timezone itself.
 */
export const warsawLabel = (iso: string | Date): string => `${warsawDay(iso)}, ${warsawTime(iso)}`
